import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, CheckCircle,
  Lock, XCircle, ShieldAlert, FileWarning // <--- Nuevo icono para error de archivo/token
} from 'lucide-react';
import { browserPopupRedirectResolver, getRedirectResult, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebase/config';

import Login from './pages/Login';
import PlexarianLoader, { PlexarianLoaderScreen } from './components/PlexarianLoader';
import UserListStep from './pages/UserListStep';
import { createContract, logoutPlexarian, validateDocs, initializeOnboarding, verifyEmployeeToken, loadUserProfile, subscribeToUserProfile, markInvitationAsUsed, verifyClientDomain, fetchClientSpaceSession, getLeadByShortId, autoCompleteCRMLeadByDomain, createClientSpace, resolveLicenciasFromProposal, buildAnexoFromProposal, omitUndefinedDeep, getExecutives, resolveEjecutivoAsignado, isEjecutivoAsignadoValid, pickEjecutivoForContract } from './firebase/api';
import { clientPortalBlocksStaffNexus, getClientPortalKind, licenciasPortalBlockMessage } from './firebase/portalHosts';
import { IDEEO_DEMO_USER, isLocalIdeeoLicenseDemo } from './dashboard/licensePortalDemo';
import { authLogoutWaitMs, consumeGoogleRedirectPending, hasGoogleRedirectPending, isStandaloneDisplay } from './utils/pwaDisplay';
import SkillsyncShareGate from './dashboard/crm/SkillsyncShareGate';
import { isSkillsyncShareHost } from './dashboard/crm/skillsync-share-expiry';
import './App.css';
import './Nexus.css';
import './pages/Login.css';

const DocumentUpload = lazy(() => import('./pages/DocumentUpload'));
const DataReviewForm = lazy(() => import('./pages/DataReviewForm'));
const ContactForm = lazy(() => import('./pages/ContactForm'));
const LicenseSelection = lazy(() => import('./pages/LicenseSelection'));
const SuccessModal = lazy(() => import('./components/modals/SuccessModal'));
const DashboardLayout = lazy(() => import('./dashboard/DashboardLayout'));
const EmployeeOnboarding = lazy(() => import('./pages/EmployeeOnboarding'));
const ClientPortalView = lazy(() => import('./dashboard/ClientPortalView'));
const ClientSpaceHome = lazy(() => import('./pages/ClientSpaceHome'));
const ClientTrainingPortalView = lazy(() => import('./dashboard/ClientTrainingPortalView'));
const Seeder = lazy(() => import('./firebase/Seeder'));
const ProposalModeRouter = lazy(() => import('./dashboard/crm/ProposalModeRouter'));
const SkillSyncReviewHub = lazy(() => import('./dashboard/crm/SkillSyncReviewHub'));
const SmartPitchMicrosite = lazy(() => import('./dashboard/crm/SmartPitchMicrosite'));
const SkillSyncIntakeForm = lazy(() => import('./dashboard/academy/SkillSyncIntakeForm'));
const PasswordGenerator = lazy(() => import('./pages/PasswordGenerator'));
const SupportPortal = lazy(() => import('./pages/SupportPortal'));
const MigrationReportViewer = lazy(() => import('./pages/MigrationReportViewer'));
const AppointmentQualification = lazy(() => import('./pages/AppointmentQualification'));
const SupportSurvey = lazy(() => import('./pages/SupportSurvey'));
const SupportQaChecklist = lazy(() => import('./pages/SupportQaChecklist'));
const DnsSetupMicrosite = lazy(() => import('./dashboard/implementation/DnsSetupMicrosite'));
const DycQuotePublic = lazy(() => import('./pages/DycPublic').then((m) => ({ default: m.DycQuotePublic })));
const DycEnrollPublic = lazy(() => import('./pages/DycPublic').then((m) => ({ default: m.DycEnrollPublic })));
const DycConstanciaPublic = lazy(() => import('./pages/DycPublic').then((m) => ({ default: m.DycConstanciaPublic })));
const GeminiFridayRsvp = lazy(() => import('./pages/GeminiFridayRsvp'));
const DispersalPackPortal = lazy(() => import('./pages/DispersalPackPortal'));
const GooglePartnerReport = lazy(() => import('./pages/GooglePartnerReport'));
const WelcomeCouponMicrosite = lazy(() => import('./pages/WelcomeCouponMicrosite'));
const PaypalReturn = lazy(() => import('./pages/PaypalReturn'));

/** Deep link Chat/correo: capturar ?contract= antes del login / lazy Dashboard. */
try {
  const _contractDeep = String(new URLSearchParams(window.location.search).get('contract') || '').trim();
  if (_contractDeep) {
    sessionStorage.setItem('nexus_open_contract_id', _contractDeep);
    sessionStorage.setItem('nexus_open_contract_section', 'firma');
    localStorage.setItem('nexus_active_tab', 'dashboard');
    localStorage.setItem('nexus_sidebar_tab', '1');
  }
} catch (_) { /* ignore */ }

/** Rutas públicas sin login de Nexus (prospectos / clientes externos) */
const PUBLIC_UNAUTHENTICATED_MODES = new Set([
  'proposal',
  'appointment-qualification',
  'smartpitch',
  'skillsync_form',
  'skillsync-review',
  'support',
  'password',
  'migration_report',
  'migration',
  'dns-setup',
  'dyc-quote',
  'dyc-enroll',
  'dyc-constancia',
  'dispersal-pack',
  'gemini-friday',
  'google-partner-report',
  'welcome-coupon',
]);

function isPaypalReturnRoute() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === '/paypal/return') return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'paypal-return';
}

function isPaypalPayRoute() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return path === '/paypal/pay' || path.startsWith('/paypal/pay/');
}

function isPaypalPublicRoute() {
  return isPaypalReturnRoute() || isPaypalPayRoute();
}

function getQualificationTokenFromLocation() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const pathMatch = path.match(/^\/confirmar-cita\/([^/?#]+)$/i);
  if (pathMatch) return decodeURIComponent(pathMatch[1]);
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') === 'appointment-qualification' && params.get('token')) {
    return params.get('token');
  }
  return null;
}

function getSurveyTokenFromLocation() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const pathMatch = path.match(/^\/encuesta\/([^/?#]+)$/i);
  if (pathMatch) return decodeURIComponent(pathMatch[1]);
  return null;
}

function isSupportQaChecklistRoute() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === '/qa/mesa-soporte') return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'support-qa-checklist';
}

function isGeminiFridayRsvpRoute() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === '/eventos/gemini-friday') return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'gemini-friday';
}

function getWelcomeCouponTokenFromLocation() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const pathMatch = path.match(/^\/cupon\/([^/?#]+)$/i);
  if (pathMatch) {
    return decodeURIComponent(pathMatch[1]).trim().replace(/[.,;:!?)]+$/g, '');
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') === 'welcome-coupon' && params.get('token')) {
    return String(params.get('token')).trim().replace(/[.,;:!?)]+$/g, '');
  }
  return null;
}

const PUBLIC_PROPOSAL_SLUGS = {
  ganem: 'preview-gcp-ganem',
  'grupo-comercial-js': 'preview-gcp-js',
  'js-sicar': 'preview-gcp-js',
};

function getPublicProposalTokenFromLocation() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const pathShort = path.match(/^\/(?:p|propuesta)\/([^/]+)$/i);
  const params = new URLSearchParams(window.location.search);
  const shortCode = params.get('p') || (pathShort ? decodeURIComponent(pathShort[1]) : null);
  if (!shortCode) return null;
  return PUBLIC_PROPOSAL_SLUGS[String(shortCode).toLowerCase()] || null;
}

function resolvePublicRouteFromLocation() {
  const publicProposalToken = getPublicProposalTokenFromLocation();
  if (publicProposalToken) {
    return { viewMode: 'proposal', token: publicProposalToken };
  }
  if (isPaypalPayRoute()) {
    return { viewMode: 'paypal_pay', token: null };
  }
  if (isPaypalReturnRoute()) {
    return { viewMode: 'paypal_return', token: null };
  }
  if (isGeminiFridayRsvpRoute()) {
    return { viewMode: 'gemini_friday', token: null };
  }
  const qualificationToken = getQualificationTokenFromLocation();
  if (qualificationToken) {
    return { viewMode: 'appointment_qualification', token: qualificationToken };
  }
  const surveyToken = getSurveyTokenFromLocation();
  if (surveyToken) {
    return { viewMode: 'support_survey', token: surveyToken };
  }
  if (isSupportQaChecklistRoute()) {
    return { viewMode: 'support_qa_checklist', token: null };
  }
  const couponToken = getWelcomeCouponTokenFromLocation();
  if (couponToken) {
    return { viewMode: 'welcome_coupon', token: couponToken };
  }
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const token = params.get('token') || params.get('id');
  const step = (params.get('step') || '').toLowerCase().trim();
  if (mode === 'dyc-quote' && token) return { viewMode: 'dyc_quote', token };
  if (mode === 'dyc-enroll' && token) return { viewMode: 'dyc_enroll', token };
  if (mode === 'dyc-constancia' && token) return { viewMode: 'dyc_constancia', token };
  if (mode === 'dispersal-pack' && token) return { viewMode: 'dispersal_pack', token };
  if (mode === 'migration_report' && token) return { viewMode: 'migration_report', token };
  if (mode === 'google-partner-report' && token) return { viewMode: 'google_partner_report', token };
  // Lista de usuarios: pintar YA (sin esperar Firestore). Sesión staff + otra pestaña
  // de Nexus puede colgar getDoc por el lock de persistentMultipleTabManager.
  if (token && step === 'userlist') {
    return { viewMode: 'user_list_only', token };
  }
  return { viewMode: 'loading', token: null };
}

function isClientTokenFlowActive() {
  if (isPaypalPublicRoute()) return false;
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  if (mode && PUBLIC_UNAUTHENTICATED_MODES.has(mode)) return false;
  if (params.get('token') || params.get('id')) return true;
  // Vanity: ?dominio.com&step=userlist
  return Array.from(params.keys()).some(
    (key) => key.includes('.') && !['token', 'step', 'mode', 'type'].includes(key)
  );
}

function isPublicUnauthenticatedRoute(mode) {
  if (isPaypalPublicRoute()) return true;
  if (isGeminiFridayRsvpRoute()) return true;
  if (mode && PUBLIC_UNAUTHENTICATED_MODES.has(mode)) return true;
  return Boolean(
    getQualificationTokenFromLocation()
    || getSurveyTokenFromLocation()
    || getWelcomeCouponTokenFromLocation()
    || isSupportQaChecklistRoute()
  );
}

let googleRedirectRead = null;

function readGoogleRedirectOnce() {
  if (googleRedirectRead) return googleRedirectRead;
  const expectingGoogleRedirect = hasGoogleRedirectPending();
  googleRedirectRead = getRedirectResult(auth, browserPopupRedirectResolver)
    .then((result) => ({ result, error: null, expectingGoogleRedirect }))
    .catch((error) => ({ result: null, error, expectingGoogleRedirect }))
    .finally(() => {
      consumeGoogleRedirectPending();
    });
  return googleRedirectRead;
}

function App() {
  const initialPublicRoute = resolvePublicRouteFromLocation();
  // --- ESTADOS DE LA APLICACIÓN ---
  const [viewMode, setViewMode] = useState(initialPublicRoute.viewMode);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // [NUEVO] Estado para guardar el token de invitación de empleado validado
  const [inviteToken, setInviteToken] = useState(null);

  // Flujo Onboarding Cliente
  const [currentStep, setCurrentStep] = useState(1);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Datos del Formulario
  const [entityType, setEntityType] = useState('moral');
  const [extractedData, setExtractedData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [executiveData, setExecutiveData] = useState(null);
  const [prefilledData, setPreloadedData] = useState(null);
  const [clientToken, setClientToken] = useState(initialPublicRoute.token);
  const [dnsPreviewDomain, setDnsPreviewDomain] = useState(null);

  // Modales y Alertas
  const [modalConfig, setModalConfig] = useState({ isOpen: false, type: '', title: '', message: '', details: '' });
  const [alertState, setAlertState] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const authLogoutTimerRef = useRef(null);
  const googleRedirectGateRef = useRef(Promise.resolve());
  const staffSessionLockRef = useRef(false);
  /** Liga de cliente (?token= / vanity): auth staff no debe mandar a nexus ni bloquear la vista. */
  const clientTokenFlowRef = useRef(
    initialPublicRoute.viewMode === 'user_list_only'
    || initialPublicRoute.viewMode === 'onboarding'
    || initialPublicRoute.viewMode === 'dispersal_pack'
    ||     initialPublicRoute.viewMode === 'welcome_coupon'
    || initialPublicRoute.viewMode === 'paypal_return'
    || initialPublicRoute.viewMode === 'paypal_pay'
    || isClientTokenFlowActive()
  );

  const isPlexarianStaffEmail = (email) => (email || '').toLowerCase().trim().endsWith('@plexarian.com');

  useEffect(() => {
    if (viewMode === 'nexus') document.title = 'Nexus';
    else if (viewMode === 'support_portal') document.title = 'Soporte Plexarian';
    else if (viewMode === 'client_space') document.title = 'Tu espacio | Plexarian';
    else if (viewMode === 'client_licencias_portal') document.title = 'Licencias | Plexarian';
    else if (viewMode === 'client_training_portal') document.title = 'Mis Capacitaciones | Plexarian';
    else if (viewMode === 'appointment_qualification') document.title = 'Confirmar cita | Plexarian';
    else if (viewMode === 'support_survey') document.title = 'Encuesta de satisfacción | Plexarian';
    else if (viewMode === 'support_qa_checklist') document.title = 'QA Mesa de Soporte | Plexarian';
    else if (viewMode === 'dyc_quote') document.title = 'Cotización DyC | Plexarian';
    else if (viewMode === 'dyc_enroll') document.title = 'Registro de curso | Plexarian';
    else if (viewMode === 'dyc_constancia') document.title = 'Constancia DyC | Plexarian';
    else if (viewMode === 'dispersal_pack') document.title = 'Pack de dispersión | Plexarian';
    else if (viewMode === 'gemini_friday') document.title = 'Gemini Friday | Plexarian';
    else if (viewMode === 'user_list_only') document.title = 'Carga de usuarios | Plexarian';
    else if (viewMode === 'migration_report') document.title = 'Auditoría de migración | Plexarian';
    else if (viewMode === 'google_partner_report') document.title = 'Red Bull · Partner Google | Plexarian';
    else if (viewMode === 'welcome_coupon') document.title = 'Cupón de bienvenida | Plexarian';
    else if (viewMode === 'paypal_return') document.title = 'Pago recibido | Plexarian';
    else if (viewMode === 'paypal_pay') document.title = 'Pago | Plexarian';
    else document.title = 'Onboarding | Plexarian Cloud';
  }, [viewMode]);

  useEffect(() => {
    import('./dashboard/DashboardLayout');
  }, []);

  // --- TEMPORIZADOR DE SEGURIDAD PARA CARGA COLGADA ---
  useEffect(() => {
    if (viewMode !== 'loading' || clientTokenFlowRef.current) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      const ready = Promise.resolve(auth.authStateReady?.() || Promise.resolve());
      const cap = Promise.race([
        ready,
        new Promise((resolve) => setTimeout(resolve, isStandaloneDisplay() ? 8000 : 2000))
      ]);
      cap
        .then(() => googleRedirectGateRef.current)
        .then(() => {
          if (cancelled || auth.currentUser) return;
          setAlertState(null);
          setViewMode('login');
        });
    }, 2500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [viewMode]);

  // --- LÓGICA DE SEGURIDAD (ZERO TRUST) ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const tokenFromUrl = params.get('token');
    const typeFromUrl = params.get('type') || (mode === 'employee_invite' ? 'employee' : null); // 'employee' o null
    const host = window.location.hostname;
    const path = window.location.pathname.replace(/\/$/, '') || '/';

    // 0. RSVP GEMINI FRIDAY (público, sin login) — invitaciones de campaña
    if (isGeminiFridayRsvpRoute()) {
      setViewMode('gemini_friday');
      return;
    }

    if (isPaypalPayRoute()) {
      setViewMode('paypal_pay');
      return;
    }

    if (isPaypalReturnRoute()) {
      setViewMode('paypal_return');
      return;
    }

    // 0b. CUESTIONARIO DE CITA (público, sin login) — prioridad máxima
    const qualificationToken = getQualificationTokenFromLocation();
    if (qualificationToken) {
      if (mode === 'appointment-qualification' && window.history.replaceState) {
        window.history.replaceState({}, document.title, `/confirmar-cita/${qualificationToken}`);
      }
      setViewMode('appointment_qualification');
      setClientToken(qualificationToken);
      return;
    }

    const surveyToken = getSurveyTokenFromLocation();
    if (surveyToken) {
      setViewMode('support_survey');
      setClientToken(surveyToken);
      return;
    }

    if (isSupportQaChecklistRoute()) {
      setViewMode('support_qa_checklist');
      return;
    }

    if (mode === 'dyc-quote' && tokenFromUrl) {
      setViewMode('dyc_quote');
      setClientToken(tokenFromUrl);
      return;
    }
    if (mode === 'dyc-enroll' && tokenFromUrl) {
      setViewMode('dyc_enroll');
      setClientToken(tokenFromUrl);
      return;
    }
    if (mode === 'dyc-constancia' && (tokenFromUrl || params.get('id'))) {
      setViewMode('dyc_constancia');
      setClientToken(tokenFromUrl || params.get('id'));
      return;
    }
    if (mode === 'dispersal-pack' && tokenFromUrl) {
      setViewMode('dispersal_pack');
      setClientToken(tokenFromUrl);
      return;
    }

    const couponToken = getWelcomeCouponTokenFromLocation();
    if (couponToken) {
      if (mode === 'welcome-coupon' && window.history.replaceState) {
        window.history.replaceState({}, document.title, `/cupon/${couponToken}`);
      }
      setViewMode('welcome_coupon');
      setClientToken(couponToken);
      return;
    }

    // Canonical: portal de soporte solo en soporte.plexarian.com
    const isNexusProdHost = host === 'nexus.plexarian.com' || host === 'www.nexus.plexarian.com';
    if (isNexusProdHost && (path === '/soporte' || path.startsWith('/soporte/') || mode === 'support')) {
      const target = new URL('https://soporte.plexarian.com/');
      if (params.toString()) target.search = params.toString();
      window.location.replace(target.toString());
      return;
    }

    // 1. MODO MIGRACIÓN (HERRAMIENTAS DB)
    if (mode === 'migration') {
      setViewMode('migration');
      return;
    }

    // [NUEVO] MODO PROPUESTA (Microsite Comercial)
    const proposalPreview = params.get('preview') === '1' || params.get('preview') === 'ge';
    if (mode === 'proposal' && (tokenFromUrl || proposalPreview)) {
      setViewMode('proposal');
      setClientToken(proposalPreview ? 'preview' : tokenFromUrl);
      return;
    }

    // [NUEVO] MODO SMARTPITCH (Microsite Estratégico generado por IA)
    const pitchId = params.get('id');
    if (mode === 'smartpitch' && pitchId) {
      setViewMode('smartpitch');
      setClientToken(pitchId);
      return;
    }

    // [NUEVO] MODO SKILLSYNC INTAKE (Formulario de Capacitación)
    if (mode === 'skillsync_form') {
      setViewMode('skillsync_form');
      return;
    }

    // Hub de revisión comercial SkillSync (micrositios demo).
    // En el canal de Andrea esto corre antes del login de portales.
    if (isSkillsyncShareHost(host) && mode !== 'proposal') {
      if (mode !== 'skillsync-review' && window.history.replaceState) {
        const next = new URLSearchParams(window.location.search);
        next.set('mode', 'skillsync-review');
        window.history.replaceState({}, document.title, `${window.location.pathname}?${next.toString()}`);
      }
      setViewMode('skillsync_review');
      return;
    }
    if (mode === 'skillsync-review') {
      setViewMode('skillsync_review');
      return;
    }

    // [NUEVO] MODO PLEXARIAN PASS (Generador de Contraseñas)
    if (mode === 'password' || window.location.hostname === 'password.plexarian.com') {
      setViewMode('password_generator');
      return;
    }

    // [NUEVO] MODO SOPORTE PLEXARIAN (prod: soporte.plexarian.com; local: ?mode=support)
    if (window.location.hostname === 'soporte.plexarian.com' || mode === 'support') {
      setViewMode('support_portal');
      return;
    }

    const portalKindOnLoad = getClientPortalKind(window.location.hostname, window.location.search);
    if (portalKindOnLoad === 'espacio' || portalKindOnLoad === 'licencias' || portalKindOnLoad === 'capacitaciones') {
      setViewMode('login');
    }

    // [NUEVO] MODO AUDITORÍA DE MIGRACIÓN
    const reportId = params.get('id');
    if (mode === 'migration_report' && reportId) {
      setViewMode('migration_report');
      setClientToken(reportId); // Reusamos clientToken para pasar el ID
      return;
    }
    if (mode === 'google-partner-report' && reportId) {
      setViewMode('google_partner_report');
      setClientToken(reportId);
      return;
    }

    // [NUEVO] GUÍA DNS IMPLEMENTACIÓN GWS
    if (mode === 'dns-setup') {
      const previewFlag = params.get('preview');
      const domainParam = params.get('domain');
      if (previewFlag === '1' || previewFlag === 'true' || (!tokenFromUrl && domainParam)) {
        setViewMode('dns-setup');
        setClientToken('preview');
        setDnsPreviewDomain(domainParam || 'pedroreyes.net');
        return;
      }
      if (tokenFromUrl) {
        setViewMode('dns-setup');
        setClientToken(tokenFromUrl);
        setDnsPreviewDomain(domainParam || null);
        return;
      }
    }

    // [NUEVO] ENLACE CORTO DE PROPUESTA (?p=CODE o /p/cliente o /propuesta/cliente)
    const pathShort = path.match(/^\/(?:p|propuesta)\/([^/]+)$/i);
    const shortCode = params.get('p') || (pathShort ? decodeURIComponent(pathShort[1]) : null);
    const publicProposalToken = getPublicProposalTokenFromLocation();
    if (publicProposalToken) {
      setClientToken(publicProposalToken);
      setViewMode('proposal');
      return;
    }

    if (shortCode) {
      // Dejar cargando mientras resuelve DB
      setViewMode('loading');
      getLeadByShortId(shortCode).then((lead) => {
        if (lead) {
          setClientToken(lead.id);
          setViewMode('proposal');
        } else {
          console.warn("Código corto de propuesta no encontrado:", shortCode);
          setAlertState({
            type: 'error',
            title: 'Enlace no válido',
            message: 'El enlace corto de la propuesta ha expirado o no existe.',
            btnText: 'Ir a Inicio',
            action: () => window.location.href = '/'
          });
          setViewMode('loading');
        }
      });
      return;
    }

    // 2. MODO INVITACIÓN EMPLEADO (TOKEN REQUERIDO)
    if (typeFromUrl === 'employee' && tokenFromUrl) {
      verifyEmployeeToken(tokenFromUrl).then(verification => {
        if (verification.valid) {
          setInviteToken(tokenFromUrl);
          setViewMode('rh_onboarding');
        } else {
          console.warn("Invite RH inválido:", verification.error);
          setViewMode('invalid_invite');
        }
      });
      return; // No iniciamos listener de Auth en este modo
    }

    // 3. FLUJO DE TOKENS (CLIENTES / ONBOARDING / USERLIST) - PRIORIDAD ALTA
    let effectiveToken = tokenFromUrl;
    let isVanityUrl = false;

    if (!effectiveToken && !typeFromUrl) {
      // Intentar descubrir "Vanity URL" (ej. ?inmeza.com&step=userlist)
      const queryKeys = Array.from(params.keys());
      const discoveredDomain = queryKeys.find(key => key.includes('.') && !['token', 'step', 'mode', 'type'].includes(key));
      if (discoveredDomain) {
        effectiveToken = discoveredDomain;
        isVanityUrl = true;
      }
    }

    if (effectiveToken && !typeFromUrl && !isPublicUnauthenticatedRoute(mode)) {
      clientTokenFlowRef.current = true;
      setClientToken(effectiveToken);
      const stepParam = (params.get('step') || '').toLowerCase().trim();

      // userlist: la vista ya puede estar activa desde resolvePublicRouteFromLocation.
      // No dependemos de Firestore para salir de "loading" (lock multi-pestaña con sesión staff).
      if (stepParam === 'userlist') {
        setViewMode('user_list_only');
      }
      
      const validateToken = async () => {
        // Nunca omitir: staff @plexarian.com también debe poder abrir ligas de cliente.
        // Timeout: IndexedDB multi-tab puede colgar getDoc si otra pestaña tiene Nexus abierto.
        const withTimeout = (promise, ms, label) => Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error(`Timeout ${label} (${ms}ms)`)), ms))
        ]);

        try {
          const { collection, query, where, getDocs, doc, getDoc } = await import('firebase/firestore');
          let data = null;
          let docFound = false;
          let finalId = effectiveToken;

          if (isVanityUrl) {
            // Buscar por dominio en las colecciones posibles (Vanity URL)
            console.log("🔍 Buscando contrato por dominio (Vanity URL):", effectiveToken);
            
            // 1. Probar en onboarding_invitations
            const qInv = query(collection(db, "onboarding_invitations"), where("domain", "==", effectiveToken));
            const snapInv = await withTimeout(getDocs(qInv), 8000, 'vanity-invitations');
            
            if (!snapInv.empty) {
              data = snapInv.docs[0].data();
              finalId = snapInv.docs[0].id; // El ID real del documento
              docFound = true;
            } else {
              // 2. Probar en contratos
              const qCon = query(collection(db, "contratos"), where("dominio", "==", effectiveToken));
              const snapCon = await withTimeout(getDocs(qCon), 8000, 'vanity-contratos');
              if (!snapCon.empty) {
                data = snapCon.docs[0].data();
                data.sourceCollection = 'contratos';
                finalId = snapCon.docs[0].id;
                docFound = true;
              } else {
                // 3. Probar en invitations
                const qInvit = query(collection(db, "invitations"), where("domain", "==", effectiveToken));
                const snapInvit = await withTimeout(getDocs(qInvit), 8000, 'vanity-invitations-col');
                if (!snapInvit.empty) {
                  data = snapInvit.docs[0].data();
                  finalId = snapInvit.docs[0].id;
                  docFound = true;
                } else {
                  // 4. Probar en renewals_metadata (domain o customer)
                  let snapRenewals = await withTimeout(
                    getDocs(query(collection(db, "renewals_metadata"), where("domain", "==", effectiveToken))),
                    8000,
                    'vanity-renewals'
                  );
                  if (snapRenewals.empty) {
                    snapRenewals = await withTimeout(
                      getDocs(query(collection(db, "renewals_metadata"), where("customer", "==", effectiveToken))),
                      8000,
                      'vanity-renewals-customer'
                    );
                  }
                  if (!snapRenewals.empty) {
                      data = snapRenewals.docs[0].data();
                      finalId = snapRenewals.docs[0].id;
                      data.isRenewal = true;
                      docFound = true;
                  }
                }
              }
            }
          } else {
            // Búsqueda estándar por ID (token) - WATERFALL
            console.log("🔍 Validando token:", effectiveToken);

            // 1. Probar en onboarding_invitations
            const docRefInv = doc(db, "onboarding_invitations", effectiveToken);
            const snapInv = await withTimeout(getDoc(docRefInv), 8000, 'token-onboarding_invitations');

            if (snapInv.exists()) {
              data = snapInv.data();
              docFound = true;
            } else {
              // 2. Probar en contratos
              const docRefCon = doc(db, "contratos", effectiveToken);
              const snapCon = await withTimeout(getDoc(docRefCon), 8000, 'token-contratos');
              if (snapCon.exists()) {
                data = snapCon.data();
                data.sourceCollection = 'contratos';
                docFound = true;
              } else {
                // 3. Probar enInvitations (usado por el CRM)
                const docRefInvit = doc(db, "invitations", effectiveToken);
                const snapInvit = await withTimeout(getDoc(docRefInvit), 8000, 'token-invitations');
                if (snapInvit.exists()) {
                    data = snapInvit.data();
                    docFound = true;
                } else {
                    // 4. Probar en renewals_metadata
                    const docRefRenewals = doc(db, "renewals_metadata", effectiveToken);
                    const snapRenewals = await withTimeout(getDoc(docRefRenewals), 8000, 'token-renewals');
                    if (snapRenewals.exists()) {
                        data = snapRenewals.data();
                        data.isRenewal = true;
                        docFound = true;
                    }
                }
              }
            }
          }

          if (docFound) {
            setClientToken(finalId);

            const applyClientTokenView = (payload) => {
              setPreloadedData({
                clientName: payload.clientName || payload.nombre_comercial || payload.razon_social || payload.companyName,
                nombre_comercial: payload.nombre_comercial || '',
                razon_social: payload.razon_social || '',
                domain: payload.domain || payload.dominio,
                isRenewal: payload.isRenewal,
                licencias: payload.licencias || payload.licenses || payload.proposal?.opciones?.[0]?.licencias || payload.proposal?.licencias || [],
                proposal: payload.proposal || null,
                propuesta_url: payload.propuesta_url || '',
                userListSheetUrl: payload.userListSheetUrl || payload.lista_usuarios_url || '',
                lista_usuarios_url: payload.lista_usuarios_url || payload.userListSheetUrl || '',
                licensePlan: payload.licensePlan || null,
                ejecutivoAsignado: payload.ejecutivoAsignado || (payload.createdBy ? { id: payload.createdBy, email: payload.creatorEmail, name: payload.creatorName } : null),
                sourceCollection: payload.sourceCollection || ''
              });

              if (stepParam === 'userlist') {
                const mixedPlan = Boolean(
                  payload.licensePlan?.mixed
                  || (payload.licensePlan?.users || []).length
                );
                if (payload.userListStatus === 'COMPLETED' && !mixedPlan) {
                  setViewMode('onboarding_completed');
                } else {
                  setViewMode('user_list_only');
                }
              } else if (payload.status === 'COMPLETED' || payload.used === true) {
                // BLOQUEO CORRECTO DE LIGA: Verifica status COMPLETED o si ya fue usada (used: true)
                setViewMode('onboarding_completed');
              } else {
                setViewMode('onboarding');
              }
            };

            // Pintar ya (userlist no necesita ejecutivos/CRM). Enrichment opcional después.
            applyClientTokenView(data);

            if (stepParam !== 'userlist') {
              try {
                const execs = await getExecutives().catch(() => []);
                const fromInvitation = pickEjecutivoForContract({}, data, execs);
                if (fromInvitation) {
                  data.ejecutivoAsignado = fromInvitation;
                }
                const currentDomain = data.domain || data.dominio;
                if (currentDomain) {
                  const crmQuery = query(collection(db, "crm_leads"), where("domain", "==", currentDomain));
                  const crmSnap = await getDocs(crmQuery);
                  if (!crmSnap.empty) {
                    const leadData = crmSnap.docs[0].data();
                    if (leadData.proposal) {
                      data.proposal = leadData.proposal;
                    }
                    if (!isEjecutivoAsignadoValid(data.ejecutivoAsignado)) {
                      const crmExec = resolveEjecutivoAsignado(leadData, {}, execs);
                      if (crmExec) data.ejecutivoAsignado = crmExec;
                    }
                  }
                }
                applyClientTokenView(data);
              } catch (crmErr) {
                console.error("Error enriqueciendo token (invitación/CRM):", crmErr);
              }
            }
          } else {
            console.warn("❌ Token inválido o no existe en la DB");
            // userlist ya visible con contractId de la URL: no mandar a login/nexus
            if (stepParam === 'userlist') return;
            if (!staffSessionLockRef.current && !isPlexarianStaffEmail(auth.currentUser?.email)) {
              setViewMode('login');
            } else if (!clientTokenFlowRef.current) {
              setViewMode('nexus');
            }
          }
        } catch (err) {
          console.error("🔥 Error validando token:", err);
          // Si es userlist, la UI ya está; el upload usa contractId de la URL vía API.
          if (stepParam === 'userlist') return;
          if (!staffSessionLockRef.current && !isPlexarianStaffEmail(auth.currentUser?.email)) {
            setViewMode('login');
          } else if (!clientTokenFlowRef.current) {
            setViewMode('nexus');
          }
        }
      };

      validateToken();
      // No retornamos aquí para permitir que Auth corra en paralelo si es necesario, 
      // pero el viewMode de arriba ganará por ser asíncrono y específico.
    }

    // 4. FLUJO NORMAL (AUTH Y SUSCRIPCIONES)
    let unsubscribeProfile = null;

    // Una sola lectura: StrictMode monta el efecto dos veces y la segunda
    // consumiría el redirect vacío. El login espera esta promesa.
    let redirectHandled = false;
    const redirectResultPromise = readGoogleRedirectOnce();
    googleRedirectGateRef.current = redirectResultPromise;
    redirectResultPromise.then(async ({ result, error, expectingGoogleRedirect }) => {
      if (redirectHandled) return;
      if (result?.user) {
        setProfileLoading(true);
        console.log('[AUTH] Google redirect completado:', result.user.email);
        if (window.history.replaceState) {
          const clean = window.location.pathname + (window.location.hash || '');
          window.history.replaceState({}, document.title, clean);
        }
        return;
      }
      const err = error;
      if (!err) {
        if (expectingGoogleRedirect) console.warn('[AUTH] Volviste de Google sin sesión.');
        return;
      }
      if (!err.code || err.code === 'auth/no-auth-event') return;

      if (
        err.code === 'auth/provider-already-linked'
        || err.code === 'auth/credential-already-in-use'
      ) {
        console.warn('[AUTH] getRedirectResult recuperable — sin modal:', err.code);
        if (!auth.currentUser) {
          try { await signOut(auth); } catch (_) { /* ignore */ }
        }
        return;
      }

      console.error('[AUTH] getRedirectResult:', err);
      if (auth.currentUser) return;
      const isSupportHost =
        window.location.hostname === 'soporte.plexarian.com'
        || new URLSearchParams(window.location.search).get('mode') === 'support';
      if (!isSupportHost && expectingGoogleRedirect) {
        setAlertState({
          type: 'error',
          title: 'Error al volver de Google',
          message: `${err.code || 'auth-error'}: ${err.message || 'Reintenta con ventana normal (no incógnito).'}`,
          btnText: 'Entendido',
          action: () => setViewMode('login')
        });
        setViewMode('login');
      }
    });

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (getSurveyTokenFromLocation() || getQualificationTokenFromLocation()) {
        return;
      }
      if (isSupportQaChecklistRoute()) {
        return;
      }

      if (authLogoutTimerRef.current) {
        clearTimeout(authLogoutTimerRef.current);
        authLogoutTimerRef.current = null;
      }

      // A. STAFF LOGUEADO
      if (firebaseUser) {
        setProfileLoading(true);
        const email = (firebaseUser.email || '').toLowerCase().trim();
        const portalKindForStaff = getClientPortalKind(window.location.hostname, window.location.search);
        if (clientPortalBlocksStaffNexus(email, portalKindForStaff)) {
          setProfileLoading(false);
          staffSessionLockRef.current = false;
          setViewMode('login');
          return;
        }
        // Liga de cliente activa (token / vanity / userlist): no entrar a nexus ni cargar perfil staff.
        if (clientTokenFlowRef.current || ((tokenFromUrl || effectiveToken) && !typeFromUrl) || getWelcomeCouponTokenFromLocation()) {
          console.log("ℹ️ Usuario logueado pero hay un token activo. Manteniendo vista de token.");
          setProfileLoading(false);
          return;
        }

        // 🔒 SISTEMA DE RUTEO INTELIGENTE (STAFF vs CLIENTES)
        if (!email.endsWith('@plexarian.com')) {
          // Extraer el dominio del correo del visitante
          const dominioVisitante = email.split('@')[1];
          const host = window.location.hostname;
          const portalKind = getClientPortalKind(host, window.location.search);

          let validationError = null;
          if (portalKind) {
            try {
              const validacion = await verifyClientDomain(dominioVisitante);
              if (validacion.isClient) {
                const portalDomain = String(validacion.dominio || dominioVisitante).toLowerCase();
                let licenseBlocked = '';
                if (portalKind === 'licencias') {
                  try {
                    const spaceSession = await fetchClientSpaceSession();
                    licenseBlocked = licenciasPortalBlockMessage(spaceSession);
                  } catch (sessionErr) {
                    licenseBlocked = sessionErr.message || 'No pudimos confirmar tu acceso. Por favor intenta de nuevo en unos minutos.';
                  }
                }
                if (!licenseBlocked) {
                  console.log(`✅ Cliente verificado (${portalKind}): ${dominioVisitante} → ${portalDomain}`);
                  setUserRole('client');
                  setCurrentUser({
                    uid: firebaseUser.uid,
                    email,
                    domain: portalDomain,
                    displayName: firebaseUser.displayName || '',
                    photoURL: firebaseUser.photoURL || ''
                  });
                  setViewMode(
                    portalKind === 'capacitaciones'
                      ? 'client_training_portal'
                      : portalKind === 'soporte'
                        ? 'support_portal'
                        : 'client_space'
                  );
                  setProfileLoading(false);
                  return;
                }
                validationError = licenseBlocked;
              } else {
                validationError = `El dominio ${dominioVisitante} no tiene licencias activas con Plexarian.`;
              }
            } catch (err) {
              console.error('Error al verificar dominio del cliente:', err);
              validationError = err.message || 'No se pudo contactar al servidor (¿backend en :8080?).';
            }
          } else {
            validationError =
              'En localhost y nexus.plexarian.com solo entra personal Plexarian (@plexarian.com). '
              + 'Licencias: https://licencias.plexarian.com · SkillSync: https://skillsync.plexarian.com';
          }

          console.warn(`⛔ Acceso denegado: ${email} en ${host}`);
          setProfileLoading(false);
          await logoutPlexarian();
          setViewMode('login');
          setAlertState({
            type: 'error',
            title: portalKind ? 'Acceso al portal' : 'Acceso Restringido',
            message: validationError,
            btnText: 'Entendido',
            action: () => setViewMode('login')
          });
          return;
        }

        const applyStaffSession = (userData) => {
          if (clientTokenFlowRef.current) {
            setProfileLoading(false);
            return;
          }
          staffSessionLockRef.current = true;
          setUserRole(userData.role);
          setCurrentUser({ uid: firebaseUser.uid, email, ...userData });
          setViewMode('nexus');
          setProfileLoading(false);
        };

        const handleStaffDenied = (userData, reason) => {
          setProfileLoading(false);
          staffSessionLockRef.current = false;
          if (userData?.active === false || userData?.role === 'no_access') {
            logoutPlexarian();
          }
          setAlertState({
            type: 'warning',
            title: 'Acceso Revocado',
            message: reason,
            btnText: 'Salir',
            action: () => setViewMode('login')
          });
          setViewMode('login');
        };

        if (unsubscribeProfile) unsubscribeProfile();

        (async () => {
          try {
            const withTimeout = (promise, ms, label) => Promise.race([
              promise,
              new Promise((_, reject) => setTimeout(() => reject(new Error(`Timeout ${label} (${ms}ms)`)), ms))
            ]);
            const profile = await withTimeout(loadUserProfile(email), 8000, 'perfil-staff');
            if (!auth.currentUser || auth.currentUser.email?.toLowerCase() !== email) return;

            if (profile && profile.active !== false && profile.role && profile.role !== 'no_access') {
              applyStaffSession(profile);
            } else if (profile && (profile.active === false || profile.role === 'no_access')) {
              handleStaffDenied(
                profile,
                profile.role === 'no_access'
                  ? 'Tu cuenta aún no tiene acceso a Nexus asignado. Contacta a tu administrador.'
                  : 'Tu cuenta está desactivada en Nexus.'
              );
              return;
            } else {
              setProfileLoading(false);
              setAlertState({
                type: 'error',
                title: 'Perfil no encontrado',
                message: `No hay registro activo en team_members para ${email}. Ejecuta sync IAM o contacta a soporte.`,
                btnText: 'Reintentar',
                action: () => window.location.reload()
              });
              return;
            }

            unsubscribeProfile = subscribeToUserProfile(email, (userData) => {
              if (!auth.currentUser) return;
              if (userData?.active !== false && userData?.role && userData.role !== 'no_access') {
                applyStaffSession(userData);
              } else if (userData?.active === false || userData?.role === 'no_access') {
                handleStaffDenied(userData, 'Tu acceso a Nexus fue revocado.');
              }
            }, (error) => {
              console.error("Error suscripción perfil:", error);
              setProfileLoading(false);
              if (!auth.currentUser || staffSessionLockRef.current) return;
              setAlertState({
                type: 'error',
                title: 'Error de Perfil',
                message: 'No se pudo sincronizar tu perfil. Reintenta en unos segundos.',
                btnText: 'Reintentar',
                action: () => window.location.reload()
              });
            });
          } catch (err) {
            console.error('Error cargando perfil staff:', err);
            setProfileLoading(false);
            if (!auth.currentUser) return;
            setAlertState({
              type: 'error',
              title: 'Error de Perfil',
              message: 'No se pudo leer team_members. Tu sesión de Google sí está activa; recarga para reintentar la lectura.',
              btnText: 'Recargar',
              action: () => window.location.reload()
            });
          }
        })();

      } else {
        // B. SIN USUARIO — un null a mitad de sesión suele ser refresh del token o IndexedDB.
        // authStateReady ya resolvió en el arranque y no protege este caso.
        const hadStaffSession = staffSessionLockRef.current;
        setProfileLoading(false);
        if (!tokenFromUrl && !effectiveToken && !isPublicUnauthenticatedRoute(mode)) {
          const goLogin = () => {
            if (auth.currentUser) return;
            staffSessionLockRef.current = false;
            setViewMode('login');
            setCurrentUser(null);
            setUserRole(null);
          };
          const waitMs = authLogoutWaitMs(hadStaffSession);
          if (hadStaffSession) {
            console.warn('[AUTH] Usuario null con sesión staff activa — esperando antes de ir al login.');
          }
          authLogoutTimerRef.current = setTimeout(() => {
            const afterRedirect = () => {
              if (auth.currentUser) return;
              if (hadStaffSession) {
                goLogin();
                return;
              }
              const ready = Promise.resolve(auth.authStateReady?.() || Promise.resolve());
              const cap = Promise.race([
                ready,
                new Promise((resolve) => setTimeout(resolve, isStandaloneDisplay() ? 8000 : 2000))
              ]);
              cap.then(goLogin);
            };
            Promise.resolve(googleRedirectGateRef.current).then(afterRedirect);
          }, waitMs);
        } else if (isPublicUnauthenticatedRoute(mode) || getQualificationTokenFromLocation() || getSurveyTokenFromLocation() || getWelcomeCouponTokenFromLocation() || isSupportQaChecklistRoute() || isGeminiFridayRsvpRoute()) {
          console.log('ℹ️ Ruta pública — sin redirección a login.');
        } else {
          console.log("⏳ Manteniendo estado de carga mientras se valida token/vanity:", effectiveToken || tokenFromUrl);
        }
      }
    });

    // Cleanup function
    return () => {
      redirectHandled = true;
      if (authLogoutTimerRef.current) clearTimeout(authLogoutTimerRef.current);
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  // --- HANDLERS (MANEJADORES DE ACCIÓN) ---

  const handleLogout = async () => {
    staffSessionLockRef.current = false;
    await logoutPlexarian();
    setUserRole(null);
    setCurrentUser(null);
    window.location.href = '/';
  };

  /**
   * [CORRECCIÓN SUPER AGRESIVA] Función para forzar la limpieza TOTAL de sesión
   * Útil cuando el estado local de Firestore (IndexedDB) se bloquea o corrompe físicamente.
   */
  const handleForceClearSession = async () => {
    try {
      console.log("🔥 [DANGER ZONE] Iniciando limpieza profunda...");
      
      staffSessionLockRef.current = false;
      // 1. Cerrar sesión en Firebase
      try { await logoutPlexarian(); } catch (e) { console.warn("Logout falló..."); }
      
      // 2. Limpiar todo el almacenamiento local simple
      localStorage.clear();
      sessionStorage.clear();

      // 3. Limpieza de IndexedDB con Promesas (Esperar a que termine)
      if (window.indexedDB && window.indexedDB.databases) {
        try {
          const dbs = await window.indexedDB.databases();
          await Promise.all(dbs.map(dbInfo => {
            return new Promise((resolve) => {
              console.log(`🗑️ Eliminando DB: ${dbInfo.name}`);
              const req = window.indexedDB.deleteDatabase(dbInfo.name);
              req.onsuccess = () => resolve();
              req.onerror = () => resolve();
              req.onblocked = () => resolve(); // Si está bloqueada, intentamos seguir
              // Timeout de seguridad para la promesa de eliminación
              setTimeout(resolve, 2000);
            });
          }));
        } catch (e) { console.error("Error IndexedDB:", e); }
      }

      // 4. Limpiar Cookies
      document.cookie.split(";").forEach(c => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      console.log("✅ Limpieza completada.");
      
      // 5. Mostrar instrucción manual antes de refrescar
      setAlertState({
        type: 'warning',
        title: 'Casi listo',
        message: 'Hemos limpiado los datos automáticos. Para asegurar que Chrome cargue la nueva configuración, por favor pulsa COMMAND + SHIFT + R (Mac) o CTRL + F5 (Windows) después de cerrar este aviso.',
        btnText: 'ENTENDIDO, RECARGAR',
        action: () => {
          window.location.href = window.location.origin + '/?clear=' + Date.now();
        }
      });
    } catch (err) {
      console.error("Error crítico:", err);
      window.location.reload();
    }
  };

  const [validationFindings, setValidationFindings] = useState({ status: 'APPROVED', analysis: '' });

  const handleValidationStart = async (files, type, priorResult) => {
    setIsLoading(true);
    setEntityType(type);
    try {
      let result = priorResult;
      if (!result?.extracted_data) {
        const formData = new FormData();
        formData.append('entityType', type);
        formData.append('csf', files.csf);
        formData.append('idOficial', files.idOficial);
        if (type === 'moral' && files.actaConstitutiva) {
          formData.append('actaConstitutiva', files.actaConstitutiva);
        }
        if (clientToken) {
          formData.append('tokenId', clientToken);
        }
        result = await validateDocs(formData);
      }
      setIsLoading(false);
      setExtractedData(result.extracted_data);
      setValidationFindings({ status: result.status, analysis: result.analisis_legal });

      let typeModal = 'APPROVED';
      let titleModal = '✅ Validación Exitosa';
      let messageModal = 'Google Vertex AI ha procesado tus documentos con éxito.';

      if (result.status === 'RISK_BLOCK') {
        typeModal = 'RISK_BLOCK';
        titleModal = '⛔ Discrepancia Crítica';
        messageModal = 'Para continuar, la Identificación debe coincidir con la Constancia de Situación Fiscal.';
      } else if (result.status === 'RISK_WARN') {
        typeModal = 'RISK_WARN';
        titleModal = '⚠️ Atención Requerida';
        messageModal = 'Se detectaron discrepancias que requieren revisión manual.';
      }

      setModalConfig({ isOpen: true, type: typeModal, title: titleModal, message: messageModal, details: result.analisis_legal });

    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setAlertState({
        type: 'error',
        title: 'Error de Conexión',
        message: `No pudimos conectar con el servidor de IA. ${e.message}`,
        btnText: 'Cerrar',
        action: () => { }
      });
    }
  };

  const handleLicenseSubmit = async (licenses, executive) => {
    setIsLoading(true);
    setExecutiveData(executive);

    try {
      const serverResult = await initializeOnboarding({
        tokenId: clientToken,
        extracted_data: extractedData,
        entityType,
        ejecutivoAsignado: executive || prefilledData?.ejecutivoAsignado,
        dominio: extractedData.dominio,
        contactos: contactData,
        validation: validationFindings, // Enviamos hallazgos de IA al servidor
        isRenewal: prefilledData?.isRenewal
      });

      const proposal = prefilledData?.proposal || null;
      const hasCommercialQuote = Boolean(
        proposal
        && (
          (Array.isArray(proposal.licencias) && proposal.licencias.length > 0)
          || (Array.isArray(proposal.serviciosAdicionales) && proposal.serviciosAdicionales.length > 0)
          || (Array.isArray(proposal.opciones) && proposal.opciones.some((opt) => (
            (Array.isArray(opt?.licencias) && opt.licencias.length > 0)
            || (Array.isArray(opt?.serviciosAdicionales) && opt.serviciosAdicionales.length > 0)
          )))
        )
      );
      const licenciasResolved = resolveLicenciasFromProposal(proposal, licenses);
      const anexoFromProposal = buildAnexoFromProposal(proposal);

      const extractedSafe = { ...(extractedData || {}) };
      if (hasCommercialQuote) {
        delete extractedSafe.licencias;
        delete extractedSafe.proposal;
        delete extractedSafe.anexo;
      }

      const finalContract = {
        entityType,
        ...extractedSafe,
        contactos: contactData,
        ...(hasCommercialQuote ? {} : {
          licencias: licenciasResolved.length > 0 ? licenciasResolved : licenses,
          proposal,
          anexo: anexoFromProposal
        }),
        propuesta_url: prefilledData?.propuesta_url || '',
        userListSheetUrl: serverResult.spreadsheetUrl || prefilledData?.userListSheetUrl || prefilledData?.lista_usuarios_url || '',
        ejecutivoAsignado: executive || prefilledData?.ejecutivoAsignado,
        driveFolderId: serverResult.folderId,
        ...(serverResult.fileLinks?.csf_url ? { csf_url: serverResult.fileLinks.csf_url } : {}),
        ...(serverResult.fileLinks?.id_url ? { id_url: serverResult.fileLinks.id_url } : {}),
        ...(serverResult.fileLinks?.acta_url ? { acta_url: serverResult.fileLinks.acta_url } : {}),
        lista_usuarios_url: serverResult.spreadsheetUrl || prefilledData?.lista_usuarios_url || prefilledData?.userListSheetUrl || '',
        pipelineStage: 'SALES_REVIEW',
        ...(hasCommercialQuote ? {} : { anexo: anexoFromProposal }),
        ai_validation_status: validationFindings.status,
        ai_analysis: validationFindings.analysis,
        onboardingCrmStatus: 'COMPLETADO',
        onboardingCompletedAt: new Date().toISOString()
      };

      let newContractId = clientToken;
      if (prefilledData && prefilledData.isRenewal) {
        const { doc, setDoc, getDoc, deleteField } = await import('firebase/firestore');
        const { canAdvanceOnboardingToLegal } = await import('./utils/contractIntegrity');
        const { buildReopenLegalQueuePatch } = await import('./utils/renewalLegalQueue');
        const docRef = doc(db, 'renewals_metadata', clientToken);
        const exec = executive || prefilledData?.ejecutivoAsignado;
        const dominioRenewal = extractedData.dominio || prefilledData?.domain || '';
        const existingSnap = await getDoc(docRef);
        const existingRenewal = existingSnap.exists() ? existingSnap.data() : {};
        const goLegal = canAdvanceOnboardingToLegal({
          ...existingRenewal,
          ...finalContract,
          onboardingCrmStatus: 'COMPLETADO',
          renewal_onboarding_completed: true
        });
        const reopenLegal = goLegal ? buildReopenLegalQueuePatch(existingRenewal) : null;
        await setDoc(docRef, {
            stage: goLegal ? 'LEGAL' : (existingRenewal.stage === 'LEGAL' || existingRenewal.stage === 'FIRMA' ? existingRenewal.stage : 'PROPUESTA'),
            renewal_onboarding_completed: true,
            renewalOnboardingCompletedAt: new Date().toISOString(),
            isRenewal: true,
            domain: dominioRenewal,
            customer: dominioRenewal || prefilledData?.domain,
            contactos: contactData,
            extracted_data: extractedData,
            driveFolderId: serverResult.folderId,
            salesPipelineVisible: true,
            onboardingCrmStatus: 'COMPLETADO',
            onboardingCompletedAt: new Date().toISOString(),
            onboardingStatus: 'COMPLETADO',
            ...(exec?.email ? {
              executiveEmail: exec.email,
              executiveName: exec.name || exec.email,
              ejecutivoAsignado: exec
            } : {}),
            ...(serverResult.fileLinks?.csf_url ? { csf_url: serverResult.fileLinks.csf_url } : {}),
            ...(serverResult.fileLinks?.id_url ? { id_url: serverResult.fileLinks.id_url } : {}),
            ...(serverResult.fileLinks?.acta_url ? { acta_url: serverResult.fileLinks.acta_url } : {}),
            ...(reopenLegal ? reopenLegal.set : {}),
            ...(reopenLegal
              ? Object.fromEntries(reopenLegal.deleteKeys.map((key) => [key, deleteField()]))
              : {})
        }, { merge: true });
      } else if (clientToken) {
        const { doc, getDoc, updateDoc } = await import('firebase/firestore');
        const docRef = doc(db, 'contratos', clientToken);
        const tokenSnap = await getDoc(docRef);
        if (tokenSnap.exists()) {
          await updateDoc(docRef, omitUndefinedDeep(finalContract));
          newContractId = clientToken;
        } else {
          newContractId = await createContract({ ...finalContract, salesPipelineVisible: true });
        }
      } else {
        newContractId = await createContract({ ...finalContract, salesPipelineVisible: true });
      }

      // Espacio Google Chat (servidor también lo intenta en initialize-onboarding; esto es respaldo en cliente)
      if (extractedData.dominio) {
        try {
          console.log(`💬 Solicitando creación de espacio en Google Chat para ${extractedData.dominio}`);
          await createClientSpace(
            newContractId,
            extractedData.dominio,
            executive?.email || prefilledData?.ejecutivoAsignado?.email,
            {
              isRenewal: Boolean(prefilledData?.isRenewal),
              renewalId: prefilledData?.isRenewal ? clientToken : undefined
            }
          );
        } catch (chatError) {
          console.warn('⚠️ No se pudo crear el espacio de Google Chat:', chatError.message);
        }
      }

      if (clientToken) {
        await markInvitationAsUsed(clientToken);
      }

      // NUEVO: Cerrar ciclo con el CRM moviéndolo al Cierre Verde
      if (extractedData.dominio) {
        await autoCompleteCRMLeadByDomain(extractedData.dominio);
      }

      setIsLoading(false);
      setIsSuccess(true);
    } catch (error) {
      setIsLoading(false);
      const msg = error.message || '';
      const actaMissing = msg.includes('Acta Constitutiva') || msg.includes('ACTA_NOT_PERSISTED');
      setAlertState({
        type: 'error',
        title: actaMissing ? 'Falta guardar el Acta Constitutiva' : 'Error al Finalizar',
        message: actaMissing
          ? `${msg} Si ya subiste el acta, regresa al paso de documentos y vuelve a validar antes de continuar.`
          : msg,
        btnText: 'Intentar de nuevo',
        action: () => { }
      });
    }
  };

  // --- RENDERIZADO DE VISTAS (VIEW MODES) ---

  // 1. CARGA INICIAL
  if (viewMode === 'loading') {
    return (
      <>
        <div className="vertex-loading-overlay">
          <div className="vertex-content nexus-boot-match">
            <span className="nexus-boot-match__dot" aria-hidden />
            <p>Cargando Nexus…</p>
          </div>
        </div>
        {alertState && (
          <SystemAlertModal
            {...alertState}
            onClose={() => {
              if (alertState.action) alertState.action();
              setAlertState(null);
            }}
          />
        )}
      </>
    );
  }

  // 2. VISTA DE MIGRACIÓN
  if (viewMode === 'migration') {
    return <Seeder />;
  }

  // 3. [NUEVO] VISTA DE ERROR DE INVITACIÓN (RH)
  if (viewMode === 'invalid_invite') {
    return (
      <div className="app-container" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center', height: '100vh', background: '#0F0518' }}>
        <div className="nexus-glass-card" style={{ padding: 50, maxWidth: 500, width: '90%', border: '1px solid #331D40' }}>
          <div style={{ background: 'rgba(255, 51, 100, 0.1)', width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
            <FileWarning size={40} color="#FF3364" />
          </div>
          <h1 style={{ fontSize: '2rem', color: 'white', margin: '0 0 10px 0' }}>Enlace No Válido</h1>
          <p style={{ color: '#94A3B8', lineHeight: 1.6 }}>
            Esta invitación para registro de colaborador ha expirado o ya fue utilizada.
          </p>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 15, marginTop: 20, fontSize: '0.9rem', color: '#CBD5E1' }}>
            Por favor, solicita un nuevo enlace a Capital Humano.
          </div>
        </div>
      </div>
    );
  }

  // 4. [ACTUALIZADO] VISTA RH (ALTA DE EMPLEADOS)
  // Ahora pasamos inviteToken como prop
  if (viewMode === 'rh_onboarding') {
    return (
      <div className="app-container">
        <div style={{ position: 'fixed', top: 20, left: 20, zIndex: 100 }}>
          <img src="/assets/logo-plexarian.svg" alt="Logo" height="40" />
        </div>
        <EmployeeOnboarding inviteToken={inviteToken} />
      </div>
    );
  }

  // 5. ACCESO DENEGADO (Token de Cliente inválido)
  if (viewMode === 'access_denied' || viewMode === 'invalid_token') {
    return (
      <div className="app-container" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center', height: '100vh', background: '#0F0518' }}>
        <div className="nexus-glass-card" style={{ padding: 50, maxWidth: 500, width: '90%', border: '1px solid #331D40' }}>
          <div style={{ background: 'rgba(255, 51, 100, 0.1)', width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
            <Lock size={40} color="#FF3364" />
          </div>
          <h1 style={{ fontSize: '2rem', color: 'white', margin: '0 0 10px 0' }}>Enlace Expirado</h1>
          <p style={{ color: '#94A3B8', lineHeight: 1.6 }}>Este enlace de invitación ya no es válido o ha caducado.</p>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 15, marginTop: 20, fontSize: '0.9rem', color: '#CBD5E1' }}>
            Por seguridad, las invitaciones tienen una vigencia de 14 días.
          </div>
          <button onClick={() => setViewMode('login')} className="btn-nexus-primary" style={{ marginTop: 30, width: '100%' }}>
            Soy Ejecutivo Plexarian
          </button>
        </div>
      </div>
    );
  }

  // 6. DASHBOARD INTERNO (Nexus)
  if (viewMode === 'nexus') {
    if (!currentUser) {
      if (auth.currentUser || profileLoading) {
        return (
          <div className="vertex-loading-overlay">
            <div className="vertex-content nexus-boot-match">
              <span className="nexus-boot-match__dot" aria-hidden />
              <p>Cargando tu perfil…</p>
            </div>
          </div>
        );
      }
      return <Login />;
    }
    return (
      <Suspense fallback={
        <div className="vertex-loading-overlay">
          <div className="vertex-content nexus-boot-match">
            <span className="nexus-boot-match__dot" aria-hidden />
            <p>Cargando Nexus…</p>
          </div>
        </div>
      }>
        <DashboardLayout role={userRole} currentUser={currentUser} onLogout={handleLogout} />
        <AnimatePresence>
          {alertState && <SystemAlertModal {...alertState} onClose={() => { if (alertState.action) alertState.action(); setAlertState(null); }} />}
        </AnimatePresence>
      </Suspense>
    );
  }

  // 11. VISTA PROPUESTA (licencias GWS o capacitación SkillSync)
  if (viewMode === 'proposal') {
    return <ProposalModeRouter token={clientToken} />;
  }

  if (viewMode === 'appointment_qualification') {
    return <AppointmentQualification token={clientToken} />;
  }

  if (viewMode === 'support_survey') {
    return <SupportSurvey token={clientToken} />;
  }

  if (viewMode === 'paypal_return' || viewMode === 'paypal_pay') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Confirmando pago…" fullScreen minHeight="100vh" />}>
        <PaypalReturn />
      </Suspense>
    );
  }

  if (viewMode === 'support_qa_checklist') {
    return <SupportQaChecklist />;
  }

  if (viewMode === 'dyc_quote') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Cargando cotización…" fullScreen minHeight="100vh" />}>
        <DycQuotePublic token={clientToken} />
      </Suspense>
    );
  }
  if (viewMode === 'dyc_enroll') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Cargando registro…" fullScreen minHeight="100vh" />}>
        <DycEnrollPublic token={clientToken} />
      </Suspense>
    );
  }
  if (viewMode === 'dyc_constancia') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Verificando constancia…" fullScreen minHeight="100vh" />}>
        <DycConstanciaPublic id={clientToken} />
      </Suspense>
    );
  }

  if (viewMode === 'dispersal_pack') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Cargando pack…" fullScreen minHeight="100vh" />}>
        <DispersalPackPortal token={clientToken} />
      </Suspense>
    );
  }

  if (viewMode === 'gemini_friday') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Cargando Gemini Friday…" fullScreen minHeight="100vh" />}>
        <GeminiFridayRsvp />
      </Suspense>
    );
  }

  if (viewMode === 'smartpitch') {
    return <SmartPitchMicrosite pitchId={clientToken} />;
  }
  if (viewMode === 'skillsync_form') {
    return <SkillSyncIntakeForm />;
  }

  if (viewMode === 'skillsync_review') {
    return (
      <SkillsyncShareGate>
        <Suspense fallback={<PlexarianLoaderScreen message="Cargando cotizaciones…" fullScreen minHeight="100vh" />}>
          <SkillSyncReviewHub />
        </Suspense>
      </SkillsyncShareGate>
    );
  }

  // 12. PLEXARIAN PASS (Generador de Contraseñas Público/Privado)
  if (viewMode === 'password_generator') {
    return <PasswordGenerator isPublic={!currentUser} />;
  }

  // 13. PORTAL DE SOPORTE PARA CLIENTES WORKSPACE
  if (viewMode === 'support_portal') {
    return <SupportPortal />;
  }

  // 14. VISOR DE AUDITORÍA DE MIGRACIÓN
  if (viewMode === 'migration_report') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Cargando auditoría…" fullScreen dark minHeight="100vh" />}>
        <MigrationReportViewer reportId={clientToken} />
      </Suspense>
    );
  }

  if (viewMode === 'google_partner_report') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Cargando reporte partner…" fullScreen dark minHeight="100vh" />}>
        <GooglePartnerReport token={clientToken} />
      </Suspense>
    );
  }

  if (viewMode === 'welcome_coupon') {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Abriendo tu cupón…" fullScreen minHeight="100vh" />}>
        <WelcomeCouponMicrosite token={clientToken} />
      </Suspense>
    );
  }

  // 14b. GUÍA DNS IMPLEMENTACIÓN
  if (viewMode === 'dns-setup') {
    return <DnsSetupMicrosite token={clientToken} previewDomain={dnsPreviewDomain} />;
  }

  // 7. PANTALLA DE LOGIN
  if (isLocalIdeeoLicenseDemo()) {
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Abriendo el recorrido de licencias…" fullScreen minHeight="100vh" />}>
        <ClientPortalView
          demoMode
          canManageDelegates
          currentUser={IDEEO_DEMO_USER}
          onLogout={() => {}}
        />
      </Suspense>
    );
  }

  if (viewMode === 'login') {
    return (
      <>
        <Login />
        <AnimatePresence>
          {alertState && <SystemAlertModal {...alertState} onClose={() => { if (alertState.action) alertState.action(); setAlertState(null); }} />}
        </AnimatePresence>
      </>
    );
  }

  // 8. Espacio de clientes (licencias.plexarian.com y ?portal=espacio)
  if (viewMode === 'client_space') {
    if (!currentUser) return <Login />;
    return (
      <Suspense fallback={<PlexarianLoaderScreen message="Abriendo tu espacio…" fullScreen minHeight="100vh" />}>
        <ClientSpaceHome currentUser={currentUser} onLogout={handleLogout} />
      </Suspense>
    );
  }

  // 8a. Portal de licencias (licencias.plexarian.com)
  if (viewMode === 'client_licencias_portal') {
    if (!currentUser) return <Login />;
    return (
      <>
        <ClientPortalView currentUser={currentUser} onLogout={handleLogout} />
        <AnimatePresence>
          {alertState && <SystemAlertModal {...alertState} onClose={() => { if (alertState.action) alertState.action(); setAlertState(null); }} />}
        </AnimatePresence>
      </>
    );
  }

  // 8b. Portal SkillSync clientes (skillsync.plexarian.com)
  if (viewMode === 'client_training_portal') {
    if (!currentUser) return <Login />;
    return (
      <>
        <ClientTrainingPortalView currentUser={currentUser} onLogout={handleLogout} />
        <AnimatePresence>
          {alertState && <SystemAlertModal {...alertState} onClose={() => { if (alertState.action) alertState.action(); setAlertState(null); }} />}
        </AnimatePresence>
      </>
    );
  }

  // 9. ONBOARDING COMPLETADO
  if (viewMode === 'onboarding_completed') {
    return (
      <div className="app-container" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center', height: '100vh', background: '#0F0518' }}>
        <div className="nexus-glass-card" style={{ padding: 50, maxWidth: 500, width: '90%', border: '1px solid #331D40' }}>
          <div style={{ background: 'rgba(51, 204, 102, 0.1)', width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
            <CheckCircle size={40} color="#33CC66" />
          </div>
          <h1 style={{ fontSize: '2rem', color: 'white', margin: '0 0 10px 0' }}>¡Proceso Completado!</h1>
          <p style={{ color: '#94A3B8', lineHeight: 1.6 }}>
            Gracias por completar tu proceso de onboarding con Plexarian. Tu ejecutivo se pondrá en contacto contigo en breve.
          </p>
          <button onClick={() => window.location.href = 'https://plexarian.com'} className="btn-nexus-primary" style={{ marginTop: 30, width: '100%', background: '#33CC66' }}>
            Ir a Plexarian
          </button>
        </div>
      </div>
    );
  }

  // 10. VISTA PRINCIPAL (CLIENTE NUEVO - ONBOARDING CON TOKEN)
  // 11. VISTA DE LISTA DE USUARIOS INDEPENDIENTE
  return (
    <div className="app-container">
      {modalConfig.isOpen && (
        <Suspense fallback={null}>
          <SuccessModal isOpen={modalConfig.isOpen} onClose={() => { setModalConfig({ ...modalConfig, isOpen: false }); setCurrentStep(3); }} {...modalConfig} />
        </Suspense>
      )}

      {/* Modal de Alerta Global */}
      <AnimatePresence>
        {alertState && (
          <SystemAlertModal
            {...alertState}
            onClose={() => {
              if (alertState.action) alertState.action();
              setAlertState(null);
            }}
          />
        )}
      </AnimatePresence>

      {isSuccess ? (
        <div className="success-screen">
          <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="success-card">
            <div className="success-icon-wrapper"><CheckCircle size={80} color="white" /></div>
            <h1>¡Todo Listo!</h1>
            <p>Hemos guardado tu información correctamente.</p>
            <p className="sub-text">Tu ejecutivo <strong>{executiveData?.name}</strong> te contactará en breve.</p>
            <button onClick={() => window.location.href = 'https://plexarian.com'} className="cta-button">IR A PLEXARIAN</button>
          </motion.div>
        </div>
      ) : (
        <div className="scroll-wrapper">
          {/* --- OLA SUPERIOR --- */}
          <svg className="wave-svg wave-top" viewBox="0 0 2880 320" preserveAspectRatio="none">
            <defs><linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" style={{ stopColor: '#FF3364', stopOpacity: 0.6 }} /><stop offset="100%" style={{ stopColor: '#331D40', stopOpacity: 0.4 }} /></linearGradient></defs>
            <path fill="url(#grad1)" d="M0,160 C480,250 960,60 1440,160 C1920,250 2400,60 2880,160 L2880,0 L0,0 Z"></path>
          </svg>

          {viewMode === 'onboarding' && (
            <Suspense fallback={<PlexarianLoaderScreen message="Cargando onboarding…" fullScreen={false} minHeight="60vh" />}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="glass-card main-layout">

              {/* --- HEADER VISUAL (DISEÑO GRANDE) --- */}
              <div className="visual-header">
                {/* 1. Logo Plexarian Gigante */}
                <img src="/assets/logo-plexarian.svg" alt="Plexarian Cloud" className="hero-logo" />

                {/* 2. Logos Google y Partner */}
                <div className="partner-row">
                  <img src="/assets/gws-gemini-light.png" alt="Google Workspace con Gemini" className="gw-logo" />
                  <img src="/assets/GWS-PP-Sell.png" alt="Premier Partner" className="badge-logo" />
                </div>

                {/* 3. Saludo Personalizado Gigante */}
                {prefilledData ? (
                  <h2 className="welcome-headline">👋 Hola, {prefilledData.clientName}</h2>
                ) : (
                  <h2 className="welcome-headline">👋 Hola, Bienvenido</h2>
                )}
              </div>

              {currentStep === 1 && (
                <>
                  <section className="hero-text-center">
                    <h1 className="main-title">Onboarding Digital</h1>
                    <p className="subtitle">Inicia tu proceso de contratación de Google Workspace en 5 minutos.</p>
                  </section>

                  <div className="features-grid-visual">
                    <div className="feature-box">
                      <div className="icon-circle"><ShieldCheck size={32} color="#FF3364" /></div>
                      <h3>Protección de Datos</h3>
                      <p>Tu documentación legal es procesada bajo los más altos estándares de encriptación.</p>
                    </div>
                    <div className="feature-box">
                      <div className="icon-circle"><img src="/assets/Gemini_SparkIcon_4C.png" alt="AI" width="28" /></div>
                      <h3>Validación Inteligente</h3>
                      <p>Utilizamos Google Vertex AI para cotejar y validar tus actas constitutivas al instante.</p>
                    </div>
                  </div>

                  <footer className="action-zone-center">
                    <div className="privacy-check">
                      <input type="checkbox" id="privacy" checked={acceptedPrivacy} onChange={(e) => setAcceptedPrivacy(e.target.checked)} />
                      <label htmlFor="privacy">He leído y acepto el <a href="https://plexarian.com/aviso-privacidad" target="_blank" rel="noreferrer">Aviso de Privacidad</a></label>
                    </div>
                    <button className={`big-start-btn ${!acceptedPrivacy ? 'locked' : ''}`} disabled={!acceptedPrivacy} onClick={() => setCurrentStep(2)}>
                      COMENZAR PROCESO
                    </button>
                  </footer>
                </>
              )}

              {currentStep === 2 && <DocumentUpload clientToken={clientToken} onNext={handleValidationStart} />}
              {currentStep === 3 && (
                <DataReviewForm
                  data={{
                    ...extractedData,
                    dominio: extractedData?.dominio || extractedData?.domain || prefilledData?.domain || '',
                    nombre_comercial: extractedData?.nombre_comercial || prefilledData?.clientName || ''
                  }}
                  entityType={entityType}
                  onConfirm={(d) => { setExtractedData(d); setCurrentStep(4); }}
                />
              )}
              {currentStep === 4 && <ContactForm onNext={(c) => { setContactData(c); setCurrentStep(5); }} />}
              {currentStep === 5 && <LicenseSelection onNext={handleLicenseSubmit} prefilledData={prefilledData} />}
              </motion.div>
            </Suspense>
          )}

          {viewMode === 'user_list_only' && (
            <div className="onboarding-main" style={{paddingTop: '40px'}}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card main-layout" style={{maxWidth: '850px', margin: '0 auto'}}>
                <UserListStep contractId={clientToken} prefilledData={prefilledData} onNext={() => setViewMode('onboarding_completed')} />
              </motion.div>
            </div>
          )}
        </div>
      )}

      {/* --- OLA INFERIOR (RESTAURADA) --- */}
      {!isSuccess && (
        <svg className="wave-svg wave-bottom" viewBox="0 0 2880 320" preserveAspectRatio="none"><path fill="#FF3364" fillOpacity="0.1" d="M0,160 C480,60 960,250 1440,160 C1920,60 2400,250 2880,160 L2880,320 L0,320 Z"></path></svg>
      )}

      {/* Loading Overlay Global */}
      {isLoading && (
        <div className="vertex-loading-overlay">
          <div className="vertex-content">
            <PlexarianLoader
              message="Validando integridad fiscal con IA…"
              size="5em"
            />
          </div>
        </div>
      )}

      {/* ESTILOS INYECTADOS + CORRECCIONES Z-INDEX */}
      <style>{`
        .glass-card.main-layout { 
            padding: 60px 40px; 
            text-align: center; 
            max-width: 900px; 
            margin-top: 40px;
            position: relative;
            z-index: 10;
        }
        
        .visual-header { display: flex; flex-direction: column; align-items: center; gap: 25px; margin-bottom: 40px; }
        .hero-logo { height: 90px; width: auto; object-fit: contain; } 
        .partner-row { display: flex; align-items: center; gap: 30px; margin-top: 10px; }
        .gw-logo { height: 45px; opacity: 0.8; }
        .badge-logo { height: 50px; }
        
        .welcome-headline { 
            font-size: 2.2rem; 
            color: #331D40; 
            font-weight: 800; 
            margin: 20px 0 0 0;
            letter-spacing: -0.5px;
        }

        .hero-text-center { margin-bottom: 50px; }
        .main-title { 
            font-size: 3.8rem; 
            background: linear-gradient(90deg, #331D40 0%, #9F2042 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            font-weight: 900; 
            margin: 0 0 15px 0;
            line-height: 1.1;
        }
        .subtitle { font-size: 1.2rem; color: #64748B; font-weight: 500; }

        .features-grid-visual { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 50px; }
        .feature-box { 
            background: white; border: 1px solid #E2E8F0; border-radius: 20px; 
            padding: 40px 30px; text-align: center; 
            box-shadow: 0 10px 30px rgba(0,0,0,0.03); transition: 0.3s;
        }
        .feature-box:hover { transform: translateY(-5px); box-shadow: 0 15px 40px rgba(0,0,0,0.08); border-color: #FF3364; }
        .icon-circle { 
            width: 60px; height: 60px; background: #FFF1F2; border-radius: 50%; 
            display: flex; align-items: center; justify-content: center; margin: 0 auto 20px auto; 
        }
        .feature-box h3 { margin: 0 0 10px 0; color: #331D40; font-size: 1.3rem; }
        .feature-box p { color: #64748B; line-height: 1.5; font-size: 0.95rem; }

        .action-zone-center { display: flex; flex-direction: column; align-items: center; gap: 20px; }
        .privacy-check { display: flex; align-items: center; gap: 10px; font-size: 0.95rem; color: #475569; }
        .privacy-check a { color: #FF3364; font-weight: 700; text-decoration: none; }
        .big-start-btn {
            background: #331D40; color: white; border: none;
            padding: 18px 60px; font-size: 1.2rem; font-weight: 800;
            border-radius: 50px; cursor: pointer; transition: 0.3s;
            box-shadow: 0 10px 25px rgba(51, 29, 64, 0.3);
        }
        .big-start-btn:hover { transform: scale(1.05); background: #FF3364; box-shadow: 0 15px 35px rgba(255, 51, 100, 0.4); }
        .big-start-btn.locked { background: #CBD5E1; cursor: not-allowed; transform: none; box-shadow: none; }
      `}</style>
    </div>
  );
}

// --- COMPONENTE DE ALERTA PERSONALIZADO (GLOBAL) ---
const SystemAlertModal = ({ type, title, message, btnText, onClose }) => {
  const isError = type === 'error';
  return (
    <div className="modal-backdrop" style={{ zIndex: 100000 }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="modal-container-pro"
        style={{ width: 400, textAlign: 'center', height: 'auto', padding: 0, overflow: 'hidden' }}
      >
        <div style={{ background: isError ? '#FEF2F2' : '#FFF7ED', padding: '30px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{
            background: 'white', width: 60, height: 60, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: 15
          }}>
            {isError ? <XCircle size={32} color="#EF4444" /> : <ShieldAlert size={32} color="#F59E0B" />}
          </div>
          <h3 style={{ margin: 0, color: isError ? '#991B1B' : '#92400E', fontSize: '1.4rem' }}>{title}</h3>
        </div>
        <div style={{ padding: '25px 30px' }}>
          <p style={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 25px 0', whiteSpace: 'pre-line' }}>
            {message}
          </p>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
              background: isError ? '#EF4444' : '#331D40', color: 'white',
              fontWeight: '700', fontSize: '1rem', cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            {btnText || 'Entendido'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default App;